import { roleHas, type AdAccountDto, type ClientDto } from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  columnFilteringFeature,
  createColumnHelper,
  createFilteredRowModel,
  createSortedRowModel,
  filterFn_equalsString,
  filterFn_includesString,
  globalFilteringFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  tableFeatures,
  useTable,
  type ColumnFiltersState,
  type SortingState,
} from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ExternalLink,
  MoreHorizontal,
  Plus,
  Wallet,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { ClientFilter, ClientTag } from "@/components/client-filter";
import { api, errorMessage } from "@/lib/api";
import {
  AdAccountPeopleDialog,
  CreateAdAccountDialog,
  EditAdAccountDialog,
  ResetSpendCapDialog,
} from "@/components/meta/ad-account-dialogs";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { queries } from "@/lib/queries";
import { formatMoney, formatRelative } from "@/lib/utils";

export const Route = createFileRoute("/app/$workspaceId/contas-de-anuncio")({
  component: ContasDeAnuncioPage,
});

const ACCOUNT_QUALITY_URL = "https://business.facebook.com/accountquality";
const TODOS = "__todos__";

const STATUS_VARIANT: Record<string, "success" | "warning" | "destructive" | "secondary"> = {
  ativa: "success",
  desativada: "destructive",
  pendente_pagamento: "warning",
  em_analise: "warning",
  acerto_pendente: "warning",
  periodo_carencia: "warning",
  fechamento_pendente: "secondary",
  fechada: "secondary",
  desconhecido: "secondary",
};

const ACCESS_LABEL: Record<AdAccountDto["accessType"], string> = {
  propria: "Própria",
  compartilhada: "Compartilhada",
  pessoal: "Pessoal",
};

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, basic: sortFn_basic },
  columnFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  filterFns: { includesString: filterFn_includesString, equalsString: filterFn_equalsString },
  globalFilteringFeature,
});

const helper = createColumnHelper<typeof features, AdAccountDto>();

/** Dados que as células precisam além da linha (clientes e permissões). */
interface TableMeta {
  workspaceId: string;
  clients: ClientDto[] | undefined;
  podeEditar: boolean;
  podeGerenciarBm: boolean;
  podeOcultar: boolean;
}

const columns = helper.columns([
  helper.accessor("name", {
    header: "Nome",
    sortFn: "alphanumeric",
    filterFn: "includesString",
    cell: ({ row }) => (
      <div className="min-w-44">
        <p className="font-medium">
          {row.original.name}
          {row.original.hidden ? (
            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
              Oculta
            </span>
          ) : null}
        </p>
        <p className="text-xs text-muted-foreground">
          {row.original.metaId} · {ACCESS_LABEL[row.original.accessType]}
        </p>
      </div>
    ),
  }),
  helper.accessor("statusKey", {
    header: "Status",
    sortFn: "alphanumeric",
    filterFn: "equalsString",
    cell: ({ row }) => {
      const a = row.original;
      return (
        <div className="space-y-1">
          <Badge variant={STATUS_VARIANT[a.statusKey] ?? "secondary"}>{a.statusLabel}</Badge>
          {a.disableReasonLabel ? (
            <p className="text-xs text-muted-foreground">
              {a.disableReasonLabel}
              {" · "}
              <a
                href={ACCOUNT_QUALITY_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 underline"
              >
                contestar na Meta
                <ExternalLink className="size-3" aria-hidden />
              </a>
            </p>
          ) : null}
        </div>
      );
    },
  }),
  helper.accessor((row) => row.ownerBusinessMetaId ?? "", {
    id: "business",
    header: "Business Manager",
    sortFn: "alphanumeric",
    filterFn: "equalsString",
    cell: ({ row }) =>
      row.original.ownerBusinessName ?? <span className="text-muted-foreground">—</span>,
  }),
  helper.accessor((row) => row.clientId ?? "", {
    id: "client",
    header: "Cliente",
    sortFn: "alphanumeric",
    filterFn: "equalsString",
    cell: ({ row, table }) => (
      <ClientTag
        clientId={row.original.clientId}
        clients={(table.options.meta as TableMeta).clients}
      />
    ),
  }),
  helper.accessor("currency", { header: "Moeda", sortFn: "alphanumeric" }),
  helper.accessor("timezoneName", {
    header: "Fuso",
    sortFn: "alphanumeric",
    cell: ({ getValue }) => getValue() ?? "—",
  }),
  helper.accessor("spendCapCents", {
    header: "Limite",
    sortFn: "basic",
    cell: ({ row }) =>
      row.original.spendCapCents ? (
        <span className="tabular-nums">
          {formatMoney(row.original.spendCapCents, row.original.currency)}
        </span>
      ) : (
        <span className="text-muted-foreground">sem limite</span>
      ),
  }),
  helper.accessor("amountSpentCents", {
    header: "Gasto",
    sortFn: "basic",
    cell: ({ row }) => (
      <span className="tabular-nums">
        {formatMoney(row.original.amountSpentCents, row.original.currency)}
      </span>
    ),
  }),
  helper.accessor("balanceCents", {
    header: "Saldo",
    sortFn: "basic",
    cell: ({ row }) => (
      <span className="tabular-nums">
        {formatMoney(row.original.balanceCents, row.original.currency)}
      </span>
    ),
  }),
  helper.display({
    id: "acoes",
    header: "",
    cell: ({ row, table }) => (
      <RowActions account={row.original} meta={table.options.meta as TableMeta} />
    ),
  }),
]);

function RowActions({ account, meta }: { account: AdAccountDto; meta: TableMeta }) {
  const [editar, setEditar] = useState(false);
  const [resetar, setResetar] = useState(false);
  const [pessoas, setPessoas] = useState(false);
  const podeAlterar = meta.podeEditar && account.statusKey !== "fechada";
  const queryClient = useQueryClient();
  const ocultar = useMutation({
    mutationFn: (hidden: boolean) =>
      api<{ hidden: boolean }>(
        `/api/workspaces/${meta.workspaceId}/meta/ad-accounts/${account.metaId}/hidden`,
        { method: "PUT", body: { hidden } },
      ),
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces", meta.workspaceId] });
      toast.success(
        r.hidden
          ? `"${account.name}" foi ocultada. Nada mudou na Meta.`
          : `"${account.name}" voltou a aparecer.`,
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={`Ações para ${account.name}`}>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setPessoas(true)}>Pessoas da conta</DropdownMenuItem>
          {podeAlterar ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setEditar(true)}>
                Editar nome e limite
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!account.spendCapCents} onSelect={() => setResetar(true)}>
                Resetar limite
              </DropdownMenuItem>
            </>
          ) : null}
          {meta.podeOcultar ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => ocultar.mutate(!account.hidden)}>
                {account.hidden ? "Mostrar conta" : "Ocultar conta"}
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <EditAdAccountDialog
        open={editar}
        onOpenChange={setEditar}
        workspaceId={meta.workspaceId}
        account={account}
      />
      <ResetSpendCapDialog
        open={resetar}
        onOpenChange={setResetar}
        workspaceId={meta.workspaceId}
        account={account}
      />
      <AdAccountPeopleDialog
        open={pessoas}
        onOpenChange={setPessoas}
        workspaceId={meta.workspaceId}
        account={account}
        podeGerenciar={meta.podeGerenciarBm}
      />
    </>
  );
}

const EMPTY: AdAccountDto[] = [];

function ContasDeAnuncioPage() {
  const { workspaceId } = Route.useParams();
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const workspace = useQuery(queries.workspace(workspaceId));
  const [mostrarOcultas, setMostrarOcultas] = useState(false);
  const accounts = useQuery(queries.adAccounts(workspaceId, clientId, mostrarOcultas));
  const clients = useQuery(queries.clients(workspaceId));
  const [criar, setCriar] = useState(false);
  const role = workspace.data?.role;
  const meta: TableMeta = {
    workspaceId,
    clients: clients.data,
    podeEditar: role ? roleHas(role, "adaccounts:edit") : false,
    podeGerenciarBm: role ? roleHas(role, "bm:manage") : false,
    podeOcultar: role ? roleHas(role, "meta:manage") : false,
  };

  return (
    <>
      <PageHeader
        title="Contas de anúncio"
        description="Todas as contas (próprias, compartilhadas e pessoais) dos perfis conectados."
        actions={
          <>
            <Button variant="ghost" onClick={() => setMostrarOcultas((v) => !v)}>
              {mostrarOcultas ? "Esconder ocultas" : "Mostrar ocultas"}
            </Button>
            <Button
              variant="outline"
              onClick={() => void accounts.refetch()}
              loading={accounts.isFetching && !accounts.isPending}
            >
              Atualizar
            </Button>
            {meta.podeGerenciarBm ? (
              <Button onClick={() => setCriar(true)}>
                <Plus />
                Criar conta
              </Button>
            ) : null}
          </>
        }
      />
      <Card>
        <CardContent>
          {accounts.isPending ? (
            <LoadingState rows={5} />
          ) : accounts.isError ? (
            <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />
          ) : accounts.data.length === 0 && !clientId ? (
            <EmptyState
              icon={<Wallet className="size-8" aria-hidden />}
              title="Nenhuma conta de anúncio"
              description="Conecte uma conta Meta ou aguarde a sincronização terminar."
              action={
                <Button asChild variant="outline">
                  <Link to="/app/$workspaceId/integracoes" params={{ workspaceId }}>
                    Ir para Integrações
                  </Link>
                </Button>
              }
            />
          ) : (
            <AccountsTable
              data={accounts.data}
              meta={meta}
              clientFilter={
                <ClientFilter workspaceId={workspaceId} value={clientId} onChange={setClientId} />
              }
            />
          )}
        </CardContent>
      </Card>
      <CreateAdAccountDialog open={criar} onOpenChange={setCriar} workspaceId={workspaceId} />
    </>
  );
}

function AccountsTable({
  data,
  meta,
  clientFilter,
}: {
  data: AdAccountDto[];
  meta: TableMeta;
  clientFilter: ReactNode;
}) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const table = useTable({
    features,
    columns,
    data: data.length > 0 ? data : EMPTY,
    meta,
    state: { sorting, columnFilters, globalFilter },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "includesString",
  });

  const businesses = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of data)
      if (a.ownerBusinessMetaId)
        map.set(a.ownerBusinessMetaId, a.ownerBusinessName ?? a.ownerBusinessMetaId);
    return [...map.entries()].sort((x, y) => x[1].localeCompare(y[1]));
  }, [data]);
  const statuses = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of data) map.set(a.statusKey, a.statusLabel);
    return [...map.entries()];
  }, [data]);

  const filterValue = (id: string) => {
    const f = columnFilters.find((c) => c.id === id);
    return typeof f?.value === "string" && f.value.length > 0 ? f.value : TODOS;
  };
  const setFilter = (id: string, value: string) => {
    setColumnFilters((prev) => {
      const rest = prev.filter((c) => c.id !== id);
      return value === TODOS ? rest : [...rest, { id, value }];
    });
  };

  const rows = table.getRowModel().rows;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {clientFilter}
        <Input
          placeholder="Buscar por nome ou id…"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="sm:max-w-xs"
          aria-label="Buscar contas"
        />
        <Select value={filterValue("business")} onValueChange={(v) => setFilter("business", v)}>
          <SelectTrigger className="sm:w-56" aria-label="Filtrar por Business Manager">
            <SelectValue placeholder="Business Manager" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todos os BMs</SelectItem>
            {businesses.map(([id, name]) => (
              <SelectItem key={id} value={id}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterValue("statusKey")} onValueChange={(v) => setFilter("statusKey", v)}>
          <SelectTrigger className="sm:w-48" aria-label="Filtrar por status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todos os status</SelectItem>
            {statuses.map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                const toggle = header.column.getToggleSortingHandler();
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:text-foreground"
                        onClick={toggle}
                        aria-sort={
                          sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"
                        }
                      >
                        <table.FlexRender header={header} />
                        {sorted === "asc" ? (
                          <ArrowUp className="size-3" aria-hidden />
                        ) : sorted === "desc" ? (
                          <ArrowDown className="size-3" aria-hidden />
                        ) : (
                          <ArrowUpDown className="size-3 opacity-40" aria-hidden />
                        )}
                      </button>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="py-8 text-center text-muted-foreground"
              >
                Nenhuma conta bate com a busca e os filtros.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.id}>
                {row.getAllCells().map((cell) => (
                  <TableCell key={cell.id}>
                    <table.FlexRender cell={cell} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <p className="text-xs text-muted-foreground">
        {rows.length} de {data.length} contas · última sincronização{" "}
        {formatRelative(data[0]?.syncedAt)}
      </p>
    </div>
  );
}
